import { Injectable, Injector, ComponentFactoryResolver } from '@angular/core';
import { MessagerService } from '@farris/ui-messager';
import { ModalOptions, BsModalService } from '@farris/ui-modal';
import { UIFlowchartComponent } from './task-flowchart/task-flowchart.component';
import { translate } from './services/i18n/index';
import { UiFlowchartService } from './services/ui-flowchart.service';
import { AppOptions, FrameworkService } from '@gsp-sys/rtf-common';
import { of } from 'rxjs';
import { HttpService } from '@ecp-caf/caf-common';
import { ForecastProcessPayload } from './entity/forecast-process-payload';
import { ProcessInstance } from './flow-chart';


@Injectable()
export class WFFlowchartService {

    private flowchartService: UiFlowchartService;
    private frameworkService: FrameworkService;

    constructor(
        private msgService: MessagerService,
        private injector: Injector,
        private resolver: ComponentFactoryResolver,
        private modalService: BsModalService,
    ) {
        this.flowchartService = this.injector.get(UiFlowchartService);
        this.frameworkService = this.injector.get(FrameworkService);
    }

    /**
     * 查看流程图，支持预览
     * @param payload 
     * @returns 
     */
    viewProcess(payload: ForecastProcessPayload) {
        if (!payload || !payload.dataId) {
            this.msgService.warning(this.getI18nValue('static.flowchart.noDataId'));
            return;
        }
        if (!payload || !payload.bizDefKey) {
            this.msgService.warning(this.getI18nValue('static.flowchart.noBizDefKey'));
            return;
        }
        const parameters = new Map<string, any>();
        parameters.set('dataId', payload.dataId);
        parameters.set('bizDefKey', payload.bizDefKey);
        if (payload.startMode) {
            parameters.set('startMode', payload.startMode);
        }
        if (payload.startUserId) {
            parameters.set('startUserId', payload.startUserId);
        }
        parameters.set('withTitle', true);
        const options: AppOptions = {
            appType: 'menu',
            funcId: 'WFViewFlowChart',
            appId: '',
            appEntrance: '',
            tabId: payload.dataId,
            isNewTab: true,
            queryStringParams: parameters
        };
        this.openMenu(options);
    }

    /**
     * 根据流程实例id查看流程（tab页中打开）
     * @param procInstId 流程实例ID
     */
    viewFlowChart(procInstId: string) {
        if (!procInstId) {
            this.msgService.warning(this.getI18nValue('static.flowchart.processNotFound'));
            return;
        }
        const parameters = new Map<string, any>();
        parameters.set('processId', procInstId);
        parameters.set('withTitle', true);
        const options: AppOptions = {
            appType: 'menu',
            funcId: 'WFViewFlowChart',
            appId: '',
            appEntrance: '',
            tabId: new Date().getTime().toString(),
            isNewTab: true,
            queryStringParams: parameters
        };
        this.openMenu(options);
    }

    /**
     * 根据单据内码查看流程
     * @param dataId 单据内码
     * @returns 
     */
    viewFlowChartByDataId(dataId: string) {
        if (!dataId) {
            this.msgService.warning(this.getI18nValue('static.flowchart.dataIdIsNull'));
            return;
        }
        const parameters = new Map<string, any>();
        parameters.set('dataId', dataId);
        parameters.set('withTitle', true);
        const options: AppOptions = {
            appType: 'menu',
            funcId: 'WFViewFlowChart',
            appId: '',
            appEntrance: '',
            tabId: new Date().getTime().toString(),
            isNewTab: true,
            queryStringParams: parameters
        };
        this.openMenu(options);
    }

    /**
     * 查看流程（弹框中打开）
     * @param procInstId 流程实例ID
     */
    viewFlowChartByDialog(procInstId: string, mode?: string) {
        if (!procInstId) {
            this.msgService.warning(this.getI18nValue('static.flowchart.processNotFound'));
            return;
        }
        this.flowchartService.getProcessInstanceById(procInstId).subscribe(re => {
            if (re) {
                this.viewMutiInstanceFlowChartByDialog([re]);
            }
        })
    }

    /**
     * 查看流程（弹框中打开，兼容多实例子流程）
     * @param procInstId 流程实例ID
     */
    viewMutiInstanceFlowChartByDialog(procInstList: ProcessInstance[], mode?: string) {
        let func;
        if (UIFlowchartComponent.func) {
            func = UIFlowchartComponent.func;
        }
        const options: ModalOptions = {
            title: this.getI18nValue('static.flowchart.title'),
            width: 1200,
            height: 530,
            showButtons: false,
            beforeClose: (modalRef: any) => {
                if (UIFlowchartComponent.func) {
                    window.removeEventListener('message', UIFlowchartComponent.func, false);
                }
                if (func) {
                    window.addEventListener('message', func, false);
                    UIFlowchartComponent.func = func;
                }
                return of(true);
            }
        };
        const compFactory = this.resolver.resolveComponentFactory(UIFlowchartComponent);
        const inj = Injector.create({
            providers: [{
                provide: UiFlowchartService, useFactory: (httpSvc) => {
                    return new UiFlowchartService(httpSvc);
                },
                deps: [
                    HttpService
                ]
            }], parent: this.injector
        });
        const compRef = compFactory.create(inj);
        if (procInstList && procInstList.length) {
            if (procInstList.length == 1) {
                options.showHeader = true;
                options.title = procInstList[0].name + '-v' + procInstList[0].version + '.0';
                compRef.instance.ProcInstId = procInstList[0].id;
            } else {
                options.showHeader = false;
                compRef.instance.ProcInstList = procInstList;
            }
        }
        if (mode) {
            compRef.instance.mode = mode;
        }
        compRef.instance.fill();
        const dialog = this.modalService.show(compRef, options);
        compRef.instance.dialog = dialog;
    }

    /**
     * 查看流程图。（如果作为共享的外部流程，则打开共享的查看流程）
     * @param procInstId 流程实例id
     * @param dataId 单据内码
     */
    viewFlowchartByProcInstIdAndDataId(procInstId: string, dataId: string) {
        this.flowchartService.ifThirdTask(procInstId).subscribe(ifThirdTask => {
            if (ifThirdTask === true) {//是第三方的任务
                this.flowchartService.getFsParamsByBizId(dataId).subscribe(params => {//获取查看流程图所需参数
                    if (params) {//有不往共享单据表推数据的情况，此时返回为空
                        const param = {
                            "lcsl": params[0],
                            "djnm": params[1],
                            "djlx": params[2],
                            "djbh": params[3]
                        }
                        this.flowchartService.viewFsProcessNew(param).subscribe(re => {//旧接口报错，改调新接口
                            if (re && re.value) {
                                this.openViewProcessMenu(re.value);
                            }
                        }, error => {
                            this.viewFlowChart(procInstId);
                        })
                    } else {
                        this.viewFlowChart(procInstId);
                    }
                }, error => {
                    this.viewFlowChart(procInstId);
                })
            } else {//WF的任务
                this.viewFlowChart(procInstId);
            }
        }, error => {
            this.viewFlowChart(procInstId);
        })
    }

    /**
     * 共享的任务，借用自己的查看流程菜单，iframe嵌入共享的查看流程URL
     * @param entity 任务实体，区分tab页
     * @param url 共享查看流程的URL
     */
    private openViewProcessMenu(url: string) {
        const queryParams = new Map<string, any>();
        queryParams.set("thirdTask", true);
        const entityParams = new Map<string, any>();
        entityParams.set("thirdProcessUrl", url);
        const options: AppOptions = {
            appType: 'menu',
            funcId: 'WFViewFlowChart',
            appId: '',
            appEntrance: '',
            tabId: new Date().getTime().toString(),
            isNewTab: true,
            entityParams: entityParams,
            queryStringParams: queryParams
        };
        this.openMenu(options);
    }

    /**
     * 获取i18n
     */
    private getI18nValue(name: string) {
        if (!name) { return ''; }
        var defaultLang = localStorage.getItem('languageCode');
        var langData = defaultLang ? translate[defaultLang] : translate['zh-CHS'];
        let resultVal = '';
        if (name.indexOf('.') === -1) {
            resultVal = langData[name];
        } else {
            resultVal = name.split('.').reduce((obj, key) => {
                if (obj) {
                    return obj[key];
                } else {
                    return null;
                }
            }, langData);
        }
        return resultVal;
    }

    /**
     * 判断是否inSuite环境
     */
    private isInSuite(): boolean {
        const frameWorkService = (window as any).gspframeworkService;
        if (frameWorkService && frameWorkService.rtf) {
            const extendMethod = frameWorkService.rtf.extendMethod;
            if (extendMethod && extendMethod.getExtObj()) {
                var extObj = extendMethod.getExtObj();
                const mode = extObj.iGIX4inSuiteMode();
                if (mode) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * 打开菜单，区分是否inSuite环境，用不同方式打开菜单
     * @param options 打开参数
     * @param menuTitle 菜单名称
     */
    private openMenu(options: any) {
        const menuTitle = this.getI18nValue('static.flowchart.title')
        if (this.isInSuite()) {
            options.menuTitle = menuTitle;
            var extObj = (window as any).gspframeworkService.rtf.extendMethod.getExtObj();
            extObj.iGIXMenuOpen(options);
        } else {
            this.frameworkService.openMenu$(options).subscribe(re => {

            }, error => {
                console.error(error);
            })
        }
    }
}
